
import { Vue, Component } from 'vue-property-decorator';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import ProductCombination from '@/components/views/productView/ProductCombination.vue';
import CombinationsSelectorProgressBar from '@/components/global/combinations/CombinationsSelectorProgressBar.vue';
import CombinationsConfiguratorTemplate from '@/components/global/combinations/CombinationsConfiguratorTemplate.vue';
import CombinationsProductsControls from '@/components/global/combinations/CombinationsProductsControls.vue';
import CombinationsProductTable from '@/components/global/combinations/CombinationsProductTable.vue';
import CombinationsProductDropdowns from '@/components/global/combinations/CombinationsProductDropdowns.vue';
import CombinationsTotalWidth from '@/components/global/combinations/CombinationsTotalWidth.vue';
import CombinationsWarning from '@/components/global/combinations/CombinationsWarning.vue';
import CombinationsStepInfo from '@/components/global/combinations/CombinationsStepInfo.vue';
import {
    combinationsConfiguratorSteps,
    getRequiredCombinationsChecklistFields,
} from '@/helpers/NewProject/CombinationsHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { Getter, Action } from 'vuex-class';
import { CombinationsConfiguratorFormData } from '@/interfaces/components/combinations/CombinationsConfiguratorFormData';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { CombinationsOptions } from '@/enums/global/GlobalOptions';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import { combinationsConfiguration } from '@/regex/allowedEntitiesForCombinationsGuide';
import { is } from 'cypress/types/bluebird';

@Component({
    name: 'CombinationsSelectorPopup',
    components: {
        ProductCombination,
        CombinationsSelectorProgressBar,
        CombinationsConfiguratorTemplate,
        CombinationsProductsControls,
        CombinationsProductTable,
        CombinationsProductDropdowns,
        CombinationsTotalWidth,
        CombinationsWarning,
        CombinationsStepInfo,
    },
})
export default class CombinationsSelectorPopup extends Vue {
    @Getter('configurator/activeProductForm')
    private getProductFormValues!: (productFormId: number) => ActiveProductFormValueObject | null;
    @Action('configurator/updateActiveProductFormValue')
    private updateActiveProductFormValue!: ({ pId, value, productFormId }: UpdateProductFormEntry) => void;
    private productData: ProductRowItem[] = [];
    private visible: boolean = false;
    private productsSelectedForCombination: number[] = [];
    private displayNextArrow = true;
    private displayPrevArrow = false;
    private currentStep: number = 1;
    private productRowsFormData: CombinationsConfiguratorFormData[] = [];
    private guideType: string = '';
    private secondGuideType: string = '';
    private totalWidth: number = 0;
    private combinationsConfiguratorSteps = combinationsConfiguratorSteps;
    private tableHeaderFields: any[] = [];
    private productName: string = '';
    private FilteredCombinationsOptions = CombinationsOptions;
    private useSlidingScreen: boolean = false;

    private get checklistFieldConfig() {
        return getRequiredCombinationsChecklistFields(this.configuratorType);
    }

    private get currentConfig() {
        return combinationsConfiguration.find((item) => item.products.includes(this.productName));
    }

    private get configuratorType() {
        if (!this.currentConfig) {
            return 'standard';
        }

        return this.currentConfig?.configuratorType || 'simple';
    }

    // Computed property to get the active steps based on configurator type
    private get activeSteps() {
        const totalSteps = this.currentConfig?.configuratorSteps || this.combinationsConfiguratorSteps.length;

        return this.combinationsConfiguratorSteps.slice(0, totalSteps);
    }

    private async openPopup(data: { rows: ProductRowItem[]; productName: string }) {
        this.visible = true;
        this.productData = data.rows;
        this.productName = data.productName;

        this.setCorrectPIdsForFormData();
        await this.$nextTick();
        this.renderFormData();
        this.updateArrowVisibility();

        await this.$nextTick();
        // this.high
    }

    private closePopup() {
        this.resetForm();
        this.visible = false;
    }

    private resetForm() {
        this.productData = [];
        this.productsSelectedForCombination = [];
        this.productRowsFormData = [];
        this.currentStep = 1;
        this.guideType = '';
        this.secondGuideType = '';
        this.totalWidth = 0;

        this.updateArrowVisibility();
    }

    private setCorrectPIdsForFormData(): void {
        console.log(this.checklistFieldConfig);

        // Map PIDs from checklistFieldConfig to FilteredCombinationsOptions
        const updatedOptions: any = {};
        Object.keys(this.checklistFieldConfig).forEach((key) => {
            const fieldConfig = (this.checklistFieldConfig as any)[key];
            updatedOptions[key] = fieldConfig.pid;
        });

        this.FilteredCombinationsOptions = { ...this.FilteredCombinationsOptions, ...updatedOptions };

        // Initialize table header fields with correct PIDs based on product name
        this.tableHeaderFields = Object.values(this.checklistFieldConfig);
    }

    private get requiredCombinationsChecklistFields() {
        return this.tableHeaderFields.map((field) => {
            field.options = field.options || [];

            if (field.pid !== CombinationsOptions.ASSEMBLY_POSITION_PID.toString()) {
                return field;
            }

            if (this.productsSelectedForCombination.length === 2) {
                field.options = [
                    { label: '1', value: '1' },
                    { label: '2', value: '2' },
                ];
            } else if (this.productsSelectedForCombination.length === 3) {
                field.options = [
                    { label: '1', value: '1' },
                    { label: '2', value: '2' },
                    { label: '3', value: '3' },
                ];
            } else {
                field.options = [];
            }

            return field;
        });
    }

    private get sortedProductsSelectedForCombination() {
        const positionPID = this.FilteredCombinationsOptions.ASSEMBLY_POSITION_PID.toString();

        return [...this.productsSelectedForCombination].sort((a, b) => {
            const aPositionField = this.productRowsFormData
                .find((row) => row.activeProductFormId === a)
                ?.formData.find((field) => field.fieldPID === positionPID);
            const bPositionField = this.productRowsFormData
                .find((row) => row.activeProductFormId === b)
                ?.formData.find((field) => field.fieldPID === positionPID);

            if (!aPositionField || !bPositionField) {
                return 0; // If position field not found, maintain original order
            }

            const aPos = Number(aPositionField.fieldValue);
            const bPos = Number(bPositionField.fieldValue);

            return aPos - bPos; // Sort in ascending order by position
        });
    }

    private get shouldAddCombinationInMultiposition() {
        return this.currentConfig ? this.currentConfig.shouldAddCombinationInMultiposition : true;
    }

    private get shouldShowAssemblyCode() {
        return this.currentConfig ? this.currentConfig.showCombinationType : false;
    }

    private get useSlidingScreenForCombination() {
        return !!this.currentConfig?.useSlidingScreen;
    }

    private addCombination() {
        // Only allow adding combinations if step 4 is completed
        if (!this.isStep4Completed) {
            console.warn(this.$t('Please complete all required fields before adding the combination'));
            return;
        }

        this.formatDataForMultiposition();
        this.updateMultiposition();

        this.$nextTick(() => {
            EventBus.$emit(
                EventBusEvents.addNewCombinationFromConfigurator,
                this.productsSelectedForCombination,
                this.shouldAddCombinationInMultiposition
            );
            this.closePopup();
        });
    }

    private updateMultiposition() {
        this.productsSelectedForCombination.forEach(async (rowId: number) => {
            const fields = this.productRowsFormData.find((row) => row.activeProductFormId === rowId)?.formData;

            if (!fields) return;

            fields.forEach(async (field: any) => {
                if (!field.shouldUpdateConfigurator) return;
                let timeout = 50;

                setTimeout(() => {
                    this.updateActiveProductFormValue({
                        pId: field.fieldPID,
                        value: field.fieldValue,
                        productFormId: rowId,
                    });
                }, timeout);
            });
        });
    }

    private formatDataForMultiposition() {
        this.formatTotalWidthFields({ value: this.totalWidth });
        this.formatNumberOfCombinationsField();

        // Skip guide fields and combination type for simple configurator
        if (this.activeSteps.length < 4) {
            return;
        }

        this.formatGuideTypeFieldsAndHeroFields();
        this.formatRollerShutterBoxSizeField();

        if (!this.showCombinationType) return;

        this.formatCombinationTypeField({ value: this.combinationType });
    }

    private toggleProductSelection(productId: number) {
        const index = this.productsSelectedForCombination.indexOf(productId);

        if (index === -1) {
            if (this.productsSelectedForCombination.length >= 3) {
                const firstSelectedElement = this.productsSelectedForCombination[0];
                const firstSelectedElementIndex = this.productsSelectedForCombination.findIndex(
                    (row) => row === firstSelectedElement
                );

                this.productsSelectedForCombination.splice(firstSelectedElementIndex, 1);
            }

            this.productsSelectedForCombination.push(productId);
        } else {
            this.productsSelectedForCombination.splice(index, 1);
        }

        this.totalWidth = 0;
        this.adjustOptionsForDropdowns();
    }

    private adjustOptionsForDropdowns() {
        // Update position options based on the number of selected products
        const positionPID = this.FilteredCombinationsOptions.ASSEMBLY_POSITION_PID.toString();

        // Update position options for all rows
        this.productRowsFormData.forEach((row) => {
            const positionField = row.formData.find((field) => field.fieldPID === positionPID);
            if (positionField) {
                if (this.productsSelectedForCombination.length === 2) {
                    positionField.options = [
                        { label: '1', value: '1' },
                        { label: '2', value: '2' },
                    ];
                } else if (this.productsSelectedForCombination.length === 3) {
                    positionField.options = [
                        { label: '1', value: '1' },
                        { label: '2', value: '2' },
                        { label: '3', value: '3' },
                    ];
                } else {
                    positionField.options = [];
                }
            }
        });
    }

    private getWindowDriveValue(productFormId: number): string {
        const value = this.productRowsFormData
            .find((row) => row.activeProductFormId === productFormId)
            ?.formData.find(
                (field) => field.fieldPID === this.FilteredCombinationsOptions.DRIVE_PID.toString()
            )?.fieldValue;

        return typeof value === 'string' ? value : '';
    }

    private get combinationType() {
        const driveOne = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[0]);
        const driveTwo = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[1]);
        const driveThree = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[2]);

        if (this.productsSelectedForCombination.length === 2) {
            return this.getTwoWindowCombinationType(driveOne, driveTwo);
        }

        if (this.productsSelectedForCombination.length === 3) {
            return this.getThreeWindowCombinationType(driveOne, driveTwo, driveThree);
        }

        return '';
    }

    private get showCombinationType() {
        if (!this.currentConfig) {
            return true;
        }

        return this.currentConfig.showCombinationType || this.productName === 'KARO';
    }

    private getTwoWindowCombinationType(driveOne: string, driveTwo: string): string {
        if (this.isFirstGuideSingle) {
            return 'D1';
        }

        const drivePatterns = [
            { pattern: () => driveOne === 'L' && driveTwo === 'L', type: 'D2' },
            { pattern: () => driveOne === 'L' && driveTwo === 'D', type: 'D2' },
            { pattern: () => driveOne === 'D' && driveTwo === 'D', type: 'D3' },
        ];

        const match = drivePatterns.find(({ pattern }) => pattern());
        return match ? match.type : '';
    }

    private getThreeWindowCombinationType(driveOne: string, driveTwo: string, driveThree: string): string {
        if (this.areBothGuidesSingle) {
            return 'T1';
        }

        const isAnySinglePartitionCombination = this.isSinglePartitionCombination || this.isPartitionSingleCombination;

        const threeWindowPatterns = [
            // T2 patterns
            {
                patterns: [
                    () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && isAnySinglePartitionCombination,
                    () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && isAnySinglePartitionCombination,
                    () =>
                        driveOne === 'D' && driveTwo === 'L' && driveThree === 'L' && this.isSinglePartitionCombination,
                    () =>
                        driveOne === 'L' && driveTwo === 'D' && driveThree === 'L' && this.isPartitionSingleCombination,
                ],
                type: 'T2',
            },
            // T3 patterns
            {
                patterns: [
                    () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                    () =>
                        driveOne === 'D' && driveTwo === 'L' && driveThree === 'D' && this.isSinglePartitionCombination,
                    () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                    () =>
                        driveOne === 'D' && driveTwo === 'D' && driveThree === 'L' && this.isPartitionSingleCombination,
                ],
                type: 'T3',
            },
            // T4 patterns
            {
                patterns: [
                    () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && this.areBothGuidesPartition,
                ],
                type: 'T4',
            },
            // T5 patterns
            {
                patterns: [
                    () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && this.areBothGuidesPartition,
                    () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && this.areBothGuidesPartition,
                ],
                type: 'T5',
            },
            // T6 patterns
            {
                patterns: [
                    () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && this.areBothGuidesPartition,
                ],
                type: 'T6',
            },
        ];

        for (const { patterns, type } of threeWindowPatterns) {
            if (patterns.some((pattern) => pattern())) {
                return type;
            }
        }

        return '';
    }

    private get isFirstGuideSingle() {
        return this.guideType === 'Enojno';
    }

    private get isSecondGuideSingle() {
        return this.secondGuideType === 'Enojno';
    }

    private get areBothGuidesSingle() {
        return this.isFirstGuideSingle && this.isSecondGuideSingle;
    }

    private get isFirstGuidePartition() {
        return this.guideType === 'Pregradno';
    }

    private get isSecondGuidePartition() {
        return this.secondGuideType === 'Pregradno';
    }

    private get areBothGuidesPartition() {
        return this.isFirstGuidePartition && this.isSecondGuidePartition;
    }

    private get isSinglePartitionCombination() {
        return this.isFirstGuideSingle && this.isSecondGuidePartition;
    }

    private get isPartitionSingleCombination() {
        return this.isFirstGuidePartition && this.isSecondGuideSingle;
    }

    private get noChoiceGuideValue() {
        return this.checklistFieldConfig.LEFT_GUIDE_PID?.noChoiceValue ?? 'Brez';
    }

    private get isInvalidCombination() {
        // Check if the selected template is valid
        if ((this.isFirstGuideSingle && this.isSecondGuideSingle) || !this.productsSelectedForCombination.length) {
            return false;
        }

        let isInvalid = false;

        const firstWindowDrive = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[0]);
        const secondWindowDrive = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[1]);
        const thirdWindowDrive = this.getWindowDriveValue(this.sortedProductsSelectedForCombination[2]);

        if (this.isFirstGuidePartition) {
            isInvalid = firstWindowDrive === 'D' && secondWindowDrive === 'L';
        }

        if (isInvalid) {
            return isInvalid;
        }

        if (this.isSecondGuidePartition && this.productsSelectedForCombination.length === 3) {
            isInvalid = secondWindowDrive === 'D' && thirdWindowDrive === 'L';
        }

        return isInvalid;
    }

    private get combinationsWarningMessage(): any {
        if (this.productsSelectedForCombination.length === 0) {
            return this.$t('No positions selected! Please select positions from the list.');
        }

        if (this.isInvalidCombination) {
            return this.$t(
                'When selecting a partition guide, both drives cannot be next to it. If you want to have both drives in the middle, please select a single guide.'
            );
        }

        return '';
    }

    private get activeCombinationsWarningMessage(): any {
        const allowedCombinedWidth = this.currentConfig?.maximumAssemblySize || Infinity;

        if (this.totalWidth >= allowedCombinedWidth) {
            return this.$t(
                'Please note! A box longer than 300 cm will be made in two parts and will have a joint in the middle.'
            );
        }

        return '';
    }

    private get combinedWidth() {
        let total = 0;

        this.productsSelectedForCombination.forEach((productId) => {
            const width = this.productRowsFormData
                .find((row) => row.activeProductFormId === productId)
                ?.formData.find(
                    (field) => field.fieldPID === this.FilteredCombinationsOptions.WIDTH_PID.toString()
                )?.fieldValue;

            if (!width) return;

            total += typeof width === 'string' ? parseInt(width) : width;
        });

        return total;
    }

    private get widthDifference() {
        if (this.totalWidth <= this.combinedWidth) {
            return 0;
        }

        return this.totalWidth - this.combinedWidth;
    }

    private get productFormValues() {
        return this.productData.map((product) => {
            const activeProductForm = {
                activeProductFormId: product.activeProductFormId,
                activeProductFormValues: this.getProductFormValues(product.activeProductFormId),
            };

            return activeProductForm;
        });
    }

    private get filteredProductRowsFormData(): CombinationsConfiguratorFormData[] {
        return this.productRowsFormData
            .filter((row) => this.sortedProductsSelectedForCombination.includes(row.activeProductFormId))
            .sort((a, b) => {
                return (
                    this.sortedProductsSelectedForCombination.indexOf(a.activeProductFormId) -
                    this.sortedProductsSelectedForCombination.indexOf(b.activeProductFormId)
                );
            });
    }

    private get getTableFields() {
        return this.tableHeaderFields.filter((field) => field.segment === 'table' && field.active);
    }

    private get getDropdownTypeFields() {
        return this.tableHeaderFields.filter((field) => field.segment === 'configurator' && field.active);
    }

    private get dropdownChecklistFields() {
        const guideOptionToFilter = [this.noChoiceGuideValue];
        const driveOptionToFilter = 'Nedefinirano';

        // We need to get vodila and pogon dropdown options
        if (!this.productData.length) {
            return [];
        }

        const combinationsChecklists = this.productData[0].editableFields.filter(
            (field) =>
                field.pId === this.FilteredCombinationsOptions.LEFT_GUIDE_PID ||
                field.pId === this.FilteredCombinationsOptions.RIGHT_GUIDE_PID ||
                field.pId === this.FilteredCombinationsOptions.DRIVE_PID
        );

        return combinationsChecklists.map((checklist) => {
            switch (checklist.pId) {
                case this.FilteredCombinationsOptions.LEFT_GUIDE_PID:
                case this.FilteredCombinationsOptions.RIGHT_GUIDE_PID:
                    return {
                        ...checklist,
                        checklistDropdownField: {
                            ...checklist.checklistDropdownField,
                            checklistDropdownOptions:
                                checklist?.checklistDropdownField?.checklistDropdownOptions.filter(
                                    (option) => !guideOptionToFilter.includes(option.vId)
                                ),
                        },
                    };
                case this.FilteredCombinationsOptions.DRIVE_PID:
                    return {
                        ...checklist,
                        checklistDropdownField: {
                            ...checklist.checklistDropdownField,
                            checklistDropdownOptions:
                                checklist?.checklistDropdownField?.checklistDropdownOptions.filter(
                                    (option) => option.vId !== driveOptionToFilter
                                ),
                        },
                    };
                default:
                    return checklist;
            }
        });
    }

    private get areFirstTwoWindowsOfSameHeight() {
        const validNumberOfWindows = [2, 3];

        if (!validNumberOfWindows.includes(this.productsSelectedForCombination.length)) {
            return false;
        }

        const firstProduct = this.productRowsFormData.find(
            (row) => row.activeProductFormId === this.sortedProductsSelectedForCombination[0]
        );
        const secondProduct = this.productRowsFormData.find(
            (row) => row.activeProductFormId === this.sortedProductsSelectedForCombination[1]
        );

        if (!firstProduct || !secondProduct) {
            return false;
        }
        const firstWindowHeightField = firstProduct.formData.find(
            (field) => field.fieldPID === this.FilteredCombinationsOptions.HEIGHT_PID
        );
        const secondWindowHeightField = secondProduct.formData.find(
            (field) => field.fieldPID === this.FilteredCombinationsOptions.HEIGHT_PID
        );

        if (!firstWindowHeightField || !secondWindowHeightField) {
            return false;
        }

        return firstWindowHeightField.fieldValue === secondWindowHeightField.fieldValue;
    }

    private get areLastTwoWindowsOfSameHeight() {
        if (this.productsSelectedForCombination.length !== 3) {
            return false;
        }

        const secondProduct = this.productRowsFormData.find(
            (row) => row.activeProductFormId === this.sortedProductsSelectedForCombination[1]
        );
        const thirdProduct = this.productRowsFormData.find(
            (row) => row.activeProductFormId === this.sortedProductsSelectedForCombination[2]
        );

        if (!secondProduct || !thirdProduct) {
            return false;
        }

        const firstWindowHeightField = secondProduct.formData.find(
            (field) => field.fieldPID === this.FilteredCombinationsOptions.HEIGHT_PID
        );
        const secondWindowHeightField = thirdProduct.formData.find(
            (field) => field.fieldPID === this.FilteredCombinationsOptions.HEIGHT_PID
        );

        if (!firstWindowHeightField || !secondWindowHeightField) {
            return false;
        }

        return firstWindowHeightField.fieldValue === secondWindowHeightField.fieldValue;
    }

    private get isStep1Completed(): boolean {
        return this.productsSelectedForCombination.length >= 2;
    }

    private get isStep2Completed(): boolean {
        if (!this.isStep1Completed) return false;

        // Check if all selected products have position values AND values are different for each row
        const positionPID = this.FilteredCombinationsOptions.ASSEMBLY_POSITION_PID.toString();
        const selectedProducts = this.filteredProductRowsFormData;

        // First, check if all products have a position value
        const allHavePositions = selectedProducts.every((product) => {
            const positionField = product.formData.find((field) => field.fieldPID === positionPID);
            return !!positionField?.fieldValue;
        });

        if (!allHavePositions) return false;

        // Then, check if all position values are unique
        const positionValues = selectedProducts.map((product) => {
            const positionField = product.formData.find((field) => field.fieldPID === positionPID);
            return positionField?.fieldValue;
        });

        // If we have the same number of unique values as total values, they're all different
        return new Set(positionValues).size === positionValues.length;
    }

    private get isStep3Completed(): boolean {
        if (!this.isStep2Completed) return false;

        if (this.totalWidth <= 0) return false;

        return true;
    }

    private get isStep4Completed(): boolean {
        // For simple configurator, step 4 doesn't exist, so it's always "completed"
        if (this.activeSteps.length < 4) {
            return true;
        }

        if (!this.isStep3Completed) return false;

        // Simply check if guide type is selected
        if (!this.guideType) return false;

        // Check if all selected products have drive values filled
        const drivePID = this.FilteredCombinationsOptions.DRIVE_PID.toString();
        const selectedProducts = this.filteredProductRowsFormData;

        return selectedProducts.every((product) => {
            const driveField = product.formData.find((field) => field.fieldPID === drivePID);
            return !!driveField?.fieldValue;
        });
    }

    private get currentStepDescription(): string {
        // The steps array is 0-indexed, but currentStep is 1-indexed
        const stepIndex = this.currentStep - 1;

        // Safety check in case the step doesn't exist
        if (stepIndex < 0 || stepIndex >= this.activeSteps.length) {
            return '';
        }

        return this.activeSteps[stepIndex].description;
    }

    private get isLastStep(): boolean {
        return this.currentStep === this.activeSteps.length;
    }

    // Generate an array of booleans indicating which steps are accessible
    private generateStepsAccessibilityArray(): boolean[] {
        const result: boolean[] = [];

        for (let i = 0; i < this.activeSteps.length; i++) {
            result.push(this.isStepAccessible(i));
        }

        return result;
    }

    // Returns true if the step at the given index is completed
    private isStepCompletedByIndex(stepIndex: number): boolean {
        switch (stepIndex) {
            case 0: // First step
                return this.isStep1Completed;
            case 1: // Second step
                return this.isStep2Completed;
            case 2: // Third step
                return this.isStep3Completed;
            case 3: // Fourth step (Add combination)
                return this.isStep4Completed; // Same as step 3
            default:
                return false;
        }
    }

    // Returns true if a step is accessible (based on previous step completion)
    private isStepAccessible(stepIndex: number): boolean {
        // First step is always accessible
        if (stepIndex === 0) return true;

        // For other steps, check if previous step is completed
        return this.isStepCompletedByIndex(stepIndex - 1);
    }

    private updateVodilaField({ value, vodilaNumber }: { value: string; vodilaNumber: number }) {
        switch (vodilaNumber) {
            case 1:
                this.guideType = value;

                break;
            case 2:
                this.secondGuideType = value;

                break;
            default:
                break;
        }
    }

    private updateSlidingScreen(value: boolean) {
        this.useSlidingScreen = value;
    }

    private formatTotalWidthFields({ value }: { value: number }) {
        const totalWidthPID = this.FilteredCombinationsOptions.TOTAL_WIDTH_PID.toString();

        this.productsSelectedForCombination.forEach((productId) => {
            this.onFormFieldChange({
                productId,
                fieldPID: totalWidthPID,
                value,
            });
        });
    }

    private get firstGuideComputedValue() {
        if (this.productsSelectedForCombination.length < 2) {
            return '';
        }

        return this.areFirstTwoWindowsOfSameHeight ? this.guideType : 'Enojno';
    }

    private get secondGuideComputedValue() {
        if (this.productsSelectedForCombination.length < 3) {
            return '';
        }

        return this.areLastTwoWindowsOfSameHeight ? this.secondGuideType : 'Enojno';
    }

    private get firstWindowRightGuideValue() {
        return this.firstGuideComputedValue;
    }

    private get secondWindowLeftGuideValue() {
        if (this.firstWindowRightGuideValue === 'Pregradno') {
            return this.noChoiceGuideValue;
        }

        return this.firstWindowRightGuideValue;
    }

    private get secondWindowRightGuideValue() {
        return this.secondGuideComputedValue;
    }

    private get thirdWindowLeftGuideValue() {
        if (this.secondWindowRightGuideValue === 'Pregradno') {
            return this.noChoiceGuideValue;
        }

        return this.secondWindowRightGuideValue;
    }

    private formatGuideTypeFieldsAndHeroFields() {
        // Process only first two or three windows
        const windowsToProcess = this.filteredProductRowsFormData.slice(0, this.filteredProductRowsFormData.length);

        if (![2, 3].includes(windowsToProcess.length)) {
            return;
        }

        const [firstWindow, secondWindow, thirdWindow] = windowsToProcess;

        const firstWindowId = firstWindow.activeProductFormId;
        const secondWindowId = secondWindow.activeProductFormId;
        const thirdWindowId = thirdWindow?.activeProductFormId;

        const productFormIds = {
            firstWindowId,
            secondWindowId,
            thirdWindowId,
        };

        this.formatGuideFields(productFormIds);

        if (this.useSlidingScreenForCombination) {
            this.formatHeroFieldsForSlidingScreen(productFormIds);
        }
    }

    private formatGuideFields({
        firstWindowId,
        secondWindowId,
        thirdWindowId,
    }: {
        firstWindowId: number;
        secondWindowId: number;
        thirdWindowId?: number;
    }) {
        const leftGuidePID = this.FilteredCombinationsOptions.LEFT_GUIDE_PID.toString();
        const rightGuidePID = this.FilteredCombinationsOptions.RIGHT_GUIDE_PID.toString();

        // Handle outer guides (always 'Enojno')
        // First window's left guide
        this.onFormFieldChange({
            productId: firstWindowId,
            fieldPID: leftGuidePID,
            value: 'Enojno',
        });

        // Last window's right guide
        this.onFormFieldChange({
            productId: thirdWindowId ?? secondWindowId,
            fieldPID: rightGuidePID,
            value: 'Enojno',
        });

        // Handle inner guides based on selected guide type

        // First window's right guide
        this.onFormFieldChange({
            productId: firstWindowId,
            fieldPID: rightGuidePID,
            value: this.firstWindowRightGuideValue,
        });

        // Second window's left guide
        this.onFormFieldChange({
            productId: secondWindowId,
            fieldPID: leftGuidePID,
            value: this.secondWindowLeftGuideValue,
        });

        if (thirdWindowId) {
            // Second window's right guide
            this.onFormFieldChange({
                productId: secondWindowId,
                fieldPID: rightGuidePID,
                value: this.secondWindowRightGuideValue,
            });

            // Third window's left guide
            this.onFormFieldChange({
                productId: thirdWindowId,
                fieldPID: leftGuidePID,
                value: this.thirdWindowLeftGuideValue,
            });
        }
    }

    private formatRollerShutterBoxSizeField() {
        // We need to set roller shutter box sizes for all products THE SAME ( if both are already automatic, we leave them as is )
        // If one is automatic and the other is not, we compare selected box size for the non-automatic and set both to that size
        const rollerShutterBoxSizePID = this.FilteredCombinationsOptions.ROLLER_SHUTTER_BOX_SIZE_PID.toString();
        const selectedRollerShutterBoxSizePID =
            this.FilteredCombinationsOptions.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID.toString();

        const automaticValue = 'Avtomatsko';

        // Collect box size info for all selected products
        const productBoxSizes = this.productsSelectedForCombination.map((productId) => {
            const productRow = this.productRowsFormData.find((row) => row.activeProductFormId === productId);
            const boxSizeField = productRow?.formData.find((field) => field.fieldPID === rollerShutterBoxSizePID);
            const selectedBoxSizeField = productRow?.formData.find(
                (field) => field.fieldPID === selectedRollerShutterBoxSizePID
            );

            return {
                productId,
                boxSizeValue: boxSizeField?.fieldValue || '',
                selectedBoxSizeValue: selectedBoxSizeField?.fieldValue || '',
                isAutomatic: boxSizeField?.fieldValue === automaticValue,
            };
        });

        // Check if all products are automatic
        const allAutomatic = productBoxSizes.every((product) => product.isAutomatic);

        if (allAutomatic) {
            // All are automatic, leave them as is
            return;
        }

        if (productBoxSizes.length === 0) {
            return;
        }

        // Get the largest box size value from non-automatic products
        // If selectedBoxSizeValue is numeric, compare numerically; otherwise use the first non-automatic value
        let targetBoxSizeValue: string | number = '';

        const numericBoxSizes = productBoxSizes
            .map((product) => {
                const boxValue = parseFloat(String(product.boxSizeValue));
                const selectedBoxValue = parseFloat(String(product.selectedBoxSizeValue));
                const validNumValue = isNaN(boxValue) ? selectedBoxValue : boxValue;

                return { productId: product.productId, value: validNumValue };
            })
            .filter((item) => !isNaN(item.value));

        // Check if there are any valid numeric box sizes
        if (numericBoxSizes.length === 0) {
            return;
        }

        // Use the largest numeric value
        const largest = numericBoxSizes.reduce((max, current) => (current.value > max.value ? current : max));
        targetBoxSizeValue = largest.value;

        // Set all products to the target box size (only update the box size field, not the selected box size)
        this.productsSelectedForCombination.forEach((productId) => {
            this.onFormFieldChange({
                productId,
                fieldPID: rollerShutterBoxSizePID,
                value: String(targetBoxSizeValue),
            });
        });
    }

    private formatHeroFieldsForSlidingScreen({
        firstWindowId,
        secondWindowId,
        thirdWindowId,
    }: {
        firstWindowId: number;
        secondWindowId: number;
        thirdWindowId?: number;
    }) {
        const floorMountingHeroPID = this.FilteredCombinationsOptions.FLOOR_MOUNTING_HERO_PID?.toString();
        const rightGuideHolderPID = this.FilteredCombinationsOptions.RIGHT_GUIDE_HOLDER_PID?.toString();
        const leftGuideHolderPID = this.FilteredCombinationsOptions.LEFT_GUIDE_HOLDER_PID?.toString();

        const guideHolderDefaultValue = 'Tip2 60-70 mm';

        // Handle first window
        if (this.isFirstGuidePartition) {
            this.onFormFieldChange({
                productId: firstWindowId,
                fieldPID: floorMountingHeroPID,
                value: 'Dvojni',
            });

            this.onFormFieldChange({
                productId: secondWindowId,
                fieldPID: floorMountingHeroPID,
                value: 'Brez',
            });

            this.onFormFieldChange({
                productId: firstWindowId,
                fieldPID: rightGuideHolderPID,
                value: guideHolderDefaultValue,
            });
        }

        if (this.isFirstGuideSingle) {
            this.onFormFieldChange({
                productId: firstWindowId,
                fieldPID: floorMountingHeroPID,
                value: 'Brez',
            });
        }

        // Handle second window
        if (this.isSecondGuidePartition) {
            this.onFormFieldChange({
                productId: secondWindowId,
                fieldPID: floorMountingHeroPID,
                value: 'Dvojni',
            });

            this.onFormFieldChange({
                productId: secondWindowId,
                fieldPID: rightGuideHolderPID,
                value: guideHolderDefaultValue,
            });
        }

        if (this.isSecondGuideSingle) {
            this.onFormFieldChange({
                productId: secondWindowId,
                fieldPID: floorMountingHeroPID,
                value: 'Brez',
            });
        }

        // Handle third window
        if (thirdWindowId) {
            if (this.isSecondGuidePartition) {
                this.onFormFieldChange({
                    productId: thirdWindowId,
                    fieldPID: floorMountingHeroPID,
                    value: 'Brez',
                });

                this.onFormFieldChange({
                    productId: thirdWindowId,
                    fieldPID: leftGuideHolderPID,
                    value: guideHolderDefaultValue,
                });
            }

            if (this.isSecondGuideSingle) {
                this.onFormFieldChange({
                    productId: thirdWindowId,
                    fieldPID: floorMountingHeroPID,
                    value: 'Brez',
                });
            }
        }
    }

    private formatCombinationTypeField({ value }: { value: string }) {
        let combinationTypePID = this.FilteredCombinationsOptions.COMBINATION_TYPE_PID.toString();

        if (!value) {
            value = 'Avtomatsko'; // Fallback value if value is not determined
        }

        this.productsSelectedForCombination.forEach((productId) => {
            this.onFormFieldChange({
                productId,
                fieldPID: combinationTypePID,
                value,
            });
        });
    }

    private formatNumberOfCombinationsField() {
        const numberOfCombinationsPID = this.FilteredCombinationsOptions.NUMBER_OF_COMBINATIONS_PID.toString();
        const numberOfCombinations = String(this.productsSelectedForCombination.length);

        this.productsSelectedForCombination.forEach((productId) => {
            this.onFormFieldChange({
                productId,
                fieldPID: numberOfCombinationsPID,
                value: numberOfCombinations,
            });
        });
    }

    private renderFormData() {
        this.productRowsFormData = this.productFormValues.map((activeProductForm) => {
            const formValues = activeProductForm.activeProductFormValues;
            const combinationsProductRow: any = {
                activeProductFormId: activeProductForm.activeProductFormId,
                formData: [],
                isHighlighted: false,
            };

            let isProductAlreadyInCombination = false;

            this.tableHeaderFields.forEach(
                (field: {
                    pid: any;
                    label: string;
                    shouldUpdateConfigurator: boolean;
                    type: string;
                    isReadOnly: boolean;
                    segment: string;
                    options?: any[];
                    step?: number;
                    value?: string | number | boolean;
                }) => {
                    const formValue = formValues?.[field.pid];
                    const defaultValue = field.value || formValue || '';
                    const formattedValue = field.isReadOnly ? defaultValue : '';

                    const checklistFieldInformation = {
                        fieldPID: field.pid,
                        fieldLabel: field.label,
                        fieldValue: formattedValue === 'Nedefinirano' ? '' : formattedValue,
                        shouldUpdateConfigurator: field.shouldUpdateConfigurator,
                        fieldType: field.type,
                        isReadOnly: field.isReadOnly || false,
                        segment: field.segment || 'table',
                        options: field?.options || [],
                        step: field?.step || 1,
                    };

                    if (field.pid === this.FilteredCombinationsOptions.COMBINATIONS_PID && !!formValue) {
                        isProductAlreadyInCombination = true;
                    }

                    // Highlight the product row if it is already part of a combination
                    combinationsProductRow.isHighlighted = isProductAlreadyInCombination;

                    combinationsProductRow.formData.push(checklistFieldInformation);
                }
            );

            return combinationsProductRow;
        });
    }

    private onFormFieldChange({
        productId,
        fieldPID,
        value,
    }: {
        productId: number;
        fieldPID: string;
        value: string | number;
    }) {
        const rowIndex = this.productRowsFormData.findIndex((row) => row.activeProductFormId === productId);
        if (rowIndex !== -1) {
            const fieldIndex = this.productRowsFormData[rowIndex].formData.findIndex(
                (field: { fieldPID: string }) => field.fieldPID === fieldPID
            );
            if (fieldIndex !== -1) {
                this.productRowsFormData[rowIndex].formData[fieldIndex].fieldValue = value;
            }
        }
    }

    private updateArrowVisibility() {
        // Display next arrow based on step count only - simple
        this.displayNextArrow = this.currentStep < this.activeSteps.length;

        // Display prev arrow if not on first step - simple
        this.displayPrevArrow = this.currentStep > 1;
    }

    private goToStep(step: number) {
        // Convert to 0-based index
        const stepIndex = step - 1;

        // Check if step is accessible
        if (this.isStepAccessible(stepIndex)) {
            this.currentStep = step;
            this.updateArrowVisibility();
        }
    }

    private goToNextStep() {
        if (this.currentStep < this.activeSteps.length) {
            this.currentStep++;
            this.updateArrowVisibility();

            if (this.currentStep === 3 && !this.totalWidth) {
                this.totalWidth = this.combinedWidth;
            }
        }
    }

    private goToPrevStep() {
        // Reset form related to currentStep
        switch (this.currentStep) {
            case 2:
                this.productRowsFormData.forEach((row) => {
                    row.formData.forEach((field) => {
                        if (field.fieldPID === this.FilteredCombinationsOptions.ASSEMBLY_POSITION_PID.toString()) {
                            field.fieldValue = '';
                        }
                    });
                });
                break;
            case 3:
                this.totalWidth = 0;
                break;
            case 4:
                this.guideType = '';
                this.secondGuideType = '';
                this.useSlidingScreen = false;
                this.productRowsFormData.forEach((row) => {
                    row.formData.forEach((field) => {
                        if (
                            field.fieldPID === this.FilteredCombinationsOptions.LEFT_GUIDE_PID.toString() ||
                            field.fieldPID === this.FilteredCombinationsOptions.RIGHT_GUIDE_PID.toString() ||
                            field.fieldPID === this.FilteredCombinationsOptions.DRIVE_PID.toString() ||
                            field.fieldPID === this.FilteredCombinationsOptions.COMBINATION_TYPE_PID.toString()
                        ) {
                            field.fieldValue = '';
                        }
                    });
                });
                break;
            default:
                break;
        }

        if (this.currentStep > 1) {
            this.currentStep--;
            this.updateArrowVisibility();
        }
    }

    private mounted() {
        EventBus.$on(EventBusEvents.openCombinationsConfiguratorSelectorPopup, this.openPopup);
        EventBus.$on(EventBusEvents.closeContactPopup, this.closePopup);
        this.updateArrowVisibility();
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.openCombinationsConfiguratorSelectorPopup, this.openPopup);
        EventBus.$off(EventBusEvents.closeContactPopup, this.closePopup);
    }

    private updateTotalWidth(value: number): void {
        if (typeof value !== 'number' || isNaN(value) || value < 0) {
            value = 0;
        }

        this.totalWidth = value;
    }
}
